import { fetchInsuranceQuote } from 'actions/InsuranceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getInsuranceQuoteParams } from 'lib/insurance/insuranceFlights'
import { INSURANCE_PRODUCT_ID } from 'constants/insurance'
import { getInsuranceQuoteKey } from 'lib/insurance/insuranceUtils'
import { useEffect, useMemo } from 'react'

interface Params {
  journeyPrice?: number
  departingDate?: string
  arrivalDate?: string
  destinationCountries?: Array<string>
  occupants?: App.Occupants
  options?: { disabled?: boolean }
}

export const QUOTE_STATUS = {
  DISABLED: 'disabled',
  FETCHING: 'fetching',
  AVAILABLE: 'available',
  ERROR: 'error',
} as const

type QuoteStatus = (typeof QUOTE_STATUS)[keyof typeof QUOTE_STATUS]

function useFlightsWithoutFareFamilyInsuranceQuote({
  journeyPrice,
  departingDate,
  arrivalDate,
  destinationCountries,
  occupants,
  options,
}: Params): {
  quote: App.InsuranceQuote | undefined
  quoteStatus: QuoteStatus
} {
  const dispatch = useAppDispatch()

  const params = useMemo(() => {
    if (options?.disabled || !journeyPrice) return

    return getInsuranceQuoteParams(departingDate, arrivalDate, journeyPrice, destinationCountries, occupants)
  }, [arrivalDate, departingDate, destinationCountries, occupants, options?.disabled, journeyPrice])

  const quoteKey = useMemo(() => {
    if (!params) return
    return getInsuranceQuoteKey(INSURANCE_PRODUCT_ID.CFMR, params)
  }, [params])

  useEffect(() => {
    if (!params || options?.disabled) return

    if (
      params.startDate &&
      params.endDate &&
      params.coverAmount &&
      params.destinationCountries?.length &&
      params.travellers
    ) {
      dispatch(fetchInsuranceQuote(INSURANCE_PRODUCT_ID.CFMR, params))
    }
  }, [dispatch, options?.disabled, params])

  const quotes = useAppSelector(state => state.insurance.quotes)
  const quote = useMemo(() => {
    if (!quoteKey) return
    return quotes[quoteKey]
  }, [quoteKey, quotes])

  const fetchingQuotes = useAppSelector(state => state.insurance.fetchingQuotes)
  const fetchQuoteErrors = useAppSelector(state => state.insurance.fetchQuotesErrors)

  const quoteStatus: QuoteStatus = useMemo(() => {
    if (options?.disabled) return QUOTE_STATUS.DISABLED

    if (!quoteKey || fetchQuoteErrors[quoteKey]) return QUOTE_STATUS.ERROR

    if (fetchingQuotes[quoteKey]) return QUOTE_STATUS.FETCHING

    if (!quote) return QUOTE_STATUS.ERROR

    if (quote) return QUOTE_STATUS.AVAILABLE

    return QUOTE_STATUS.ERROR
  }, [options?.disabled, quote, quoteKey, fetchingQuotes, fetchQuoteErrors])

  return {
    quote,
    quoteStatus,
  }
}

export default useFlightsWithoutFareFamilyInsuranceQuote
